import React from 'react';
import { StreamWithChatRate } from '../types';
import { TwitchUser } from '../services/twitchApi';
import { getHeatRatio, getHeatTier, isHeatReady, formatCompact } from '../utils/heat';
import { IconFlame } from './icons';

export interface StreamRowProps {
  stream: StreamWithChatRate;
  /** 1-based position in the visible list */
  rank: number;
  selected: boolean;
  /** Custom-list rows carry an eject button */
  editable: boolean;
  /** Loudest 10s rate in the visible list — scales the velocity meter */
  peakRate: number;
  onSelect: (stream: StreamWithChatRate) => void;
  onRemove?: (login: string) => void;
}

const RemoveButton: React.FC<{ login: string; onRemove: (login: string) => void }> = ({ login, onRemove }) => (
  <button
    className="row-remove"
    title="Remove from list"
    aria-label={`Remove ${login} from list`}
    onClick={(e) => {
      e.stopPropagation();
      onRemove(login);
    }}
  >
    ×
  </button>
);

/**
 * One ranked instrument in the band scanner: rack number, monitor, name, and
 * a live velocity meter. Memoised — the monitor hands back the same stream
 * object while its numbers hold still, so quiet rows skip the 2-second tick.
 */
export const StreamRow = React.memo<StreamRowProps>(({
  stream,
  rank,
  selected,
  editable,
  peakRate,
  onSelect,
  onRemove,
}) => {
  const isOffline = !stream.thumbnail_url;
  const heatReady = isHeatReady(stream);
  const heat = heatReady ? getHeatRatio(stream) : 0;
  const heatTier = heatReady ? getHeatTier(heat) : 'cold';
  const rate = stream.chatRate10s || 0;
  // sqrt scale keeps quiet chats visible next to 50 msg/s monsters
  const veloFill = peakRate > 0
    ? Math.max(4, Math.round(Math.sqrt(rate / peakRate) * 100))
    : 4;

  return (
    <div
      className={`stream-item heat-${heatTier} ${selected ? 'selected' : ''} ${isOffline ? 'offline' : ''} ${editable ? 'editable' : ''}`}
      onClick={() => onSelect(stream)}
    >
      <div className="stream-rank">{String(rank).padStart(2, '0')}</div>
      <div className="stream-thumb-box">
        {isOffline ? (
          <span className="thumb-offline">offline</span>
        ) : (
          <>
            <img
              src={stream.thumbnail_url.replace('{width}', '176').replace('{height}', '99')}
              alt=""
              loading="lazy"
              className="stream-thumbnail"
            />
            <span className="thumb-viewers" title={`${stream.viewer_count.toLocaleString()} viewers`}>
              {formatCompact(stream.viewer_count)}
            </span>
          </>
        )}
      </div>
      <div className="stream-info">
        <div className="stream-toprow">
          <span className="stream-streamer">{stream.user_name}</span>
          {!isOffline && heatReady && heatTier !== 'cold' && (
            <span
              className={`heat-badge heat-${heatTier}`}
              title={`Heat ${heat.toFixed(1)}× — chat is ${heat.toFixed(1)}× its 5-minute average`}
            >
              <IconFlame size={9} />
              {heat.toFixed(1)}×
            </span>
          )}
        </div>
        {isOffline ? (
          <div className="stream-sub offline-note">Offline</div>
        ) : (
          <div className="stream-sub" title={stream.title}>
            {stream.game_name && <span className="stream-game">{stream.game_name}</span>}
            {stream.game_name && stream.title && <span className="stream-sub-sep">·</span>}
            <span className="stream-title-text">{stream.title}</span>
          </div>
        )}
        {!isOffline && (
          <div
            className="stream-velo"
            title={heatReady
              ? `${rate.toFixed(1)} msgs/s now · ${stream.chatRate5m?.toFixed(1) || '0.0'}/s 5-min average`
              : 'Measuring chat baseline…'}
          >
            <span className={`velo ${!heatReady ? 'calibrating' : ''}`} aria-hidden>
              <span className="velo-fill" style={{ width: `${veloFill}%` }} />
            </span>
            <span className="stream-rate">{rate.toFixed(1)}/s</span>
          </div>
        )}
      </div>
      {editable && onRemove && <RemoveButton login={stream.user_login} onRemove={onRemove} />}
    </div>
  );
});
StreamRow.displayName = 'StreamRow';

/** A roster member who is not live: a bare placeholder the player can still point at */
export const makeOfflinePlaceholder = (login: string, user?: TwitchUser): StreamWithChatRate => ({
  id: login,
  user_id: user?.id || '',
  user_login: login,
  user_name: user?.display_name || login,
  game_id: '',
  game_name: '',
  type: 'live',
  title: '',
  viewer_count: 0,
  started_at: '',
  language: '',
  thumbnail_url: '',
  tag_ids: [],
  is_mature: false,
  chatRate: 0,
  messageCount: 0,
});

export interface RosterOfflineRowProps {
  login: string;
  user?: TwitchUser;
  editable: boolean;
  onSelect: (stream: StreamWithChatRate) => void;
  onRemove?: (login: string) => void;
}

export const RosterOfflineRow = React.memo<RosterOfflineRowProps>(({ login, user, editable, onSelect, onRemove }) => (
  <div
    className={`stream-item offline roster-row ${editable ? 'editable' : ''}`}
    onClick={() => onSelect(makeOfflinePlaceholder(login, user))}
  >
    <div className="stream-rank">—</div>
    <div className="stream-thumb-box">
      {user ? (
        <img src={user.profile_image_url} alt="" loading="lazy" className="roster-avatar" />
      ) : (
        <span className="thumb-offline">offline</span>
      )}
    </div>
    <div className="stream-info">
      <div className="stream-toprow">
        <span className="stream-streamer">{user?.display_name || login}</span>
      </div>
      <div className="stream-sub offline-note">Offline</div>
    </div>
    {editable && onRemove && <RemoveButton login={login} onRemove={onRemove} />}
  </div>
));
RosterOfflineRow.displayName = 'RosterOfflineRow';
